import { Autocomplete, Chip, SxProps, TextField, Theme } from "@mui/material";

/**
 * The shared waste-code chip input: an MUI freeSolo multi-Autocomplete
 * rendering the chosen codes as deletable chips, with validator-flagged codes
 * shown as red (error) chips. Purely presentational — the Waste Activity step
 * feeds it compliance-engine issues (server-validated) while the Filing
 * Notifications dialogs feed it synchronous client-side validators; both
 * reduce to the same props here.
 *
 * Typed codes commit on blur (`autoSelect`) so clicking a dialog's Save button
 * (which blurs the input) captures the pending code without pressing Enter.
 */
export function WasteCodeChipInput({
  name,
  label,
  codes,
  onChange,
  onInputChange,
  disabled = false,
  invalidCodes = [],
  error = false,
  helperText,
  placeholder,
  size,
  textFieldSx,
}: {
  name?: string;
  label: string;
  codes: string[];
  onChange: (codes: string[]) => void;
  onInputChange?: (text: string) => void;
  disabled?: boolean;
  /** Codes to flag red; matched to chips case-insensitively (trimmed, uppercased). */
  invalidCodes?: readonly string[];
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  size?: "small" | "medium";
  textFieldSx?: SxProps<Theme>;
}) {
  const flagged = new Set(invalidCodes.map((code) => code.trim().toUpperCase()));
  const isInvalid = (code: string) => flagged.has(code.trim().toUpperCase());

  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      options={[]}
      value={codes}
      disabled={disabled}
      onChange={(_, value) => onChange(value)}
      onInputChange={(_, value) => onInputChange?.(value)}
      renderTags={(value, getTagProps) =>
        value.map((code, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={code}
              size="small"
              color={isInvalid(code) ? "error" : "default"}
              {...chipProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          size={size}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          sx={textFieldSx}
        />
      )}
    />
  );
}
